import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import Button from '../../../../components/Button'
import CarouselItemForm from './CarouselItemForm'
import ErrorMessage from '../../../../components/Form/ErrorMessage'

import { setAdCarouselSelectedIndex } from '../../../../modules/actions/ads'
import { adCarouselSelectedIndexesSelector } from '../../../../modules/selectors/ads'

import {
  CAROUSEL_ITEMS,
  initialCarouselItem,
  IS_ITEM_FORM_OPENED,
  CLICK_THROUGH_LINK,
  CALL_TO_ACTION,
  CAROUSEL_ITEMS_LENGTH,
  AD_PREVIEW_CAROUSEL_INDEX_KEY
} from '../../../Facebook/AdForms/fields'

// To make it possible for component to work with nested objects
// in formik values, use the built-in formik function getIn
const CarouselMediaFields = ({ formik, isFieldsValid, onCarouselItemDelete, campaignObjective }) => {
  const dispatch = useDispatch()
  const [showErrors, setShowErrors] = useState(false)

  const { t } = useTranslation()

  const { values, errors, setFieldValue, setValues } = formik

  const carouselItems = getIn(values, `${CAROUSEL_ITEMS}`)
  const selectedItemIndexes = useSelector(adCarouselSelectedIndexesSelector)
  const selectedItemIndexKey = values[AD_PREVIEW_CAROUSEL_INDEX_KEY]
  const carouselItemsLength = values[CAROUSEL_ITEMS_LENGTH]
  const carouselItemsLengthErrors = errors[CAROUSEL_ITEMS_LENGTH]

  const selectedItemIndex = selectedItemIndexes[selectedItemIndexKey] || 0

  // Constant that represent path ot item in formik values
  const itemPath = `${CAROUSEL_ITEMS}[${selectedItemIndex}]`

  const handleAddAnotherItem = useCallback(() => {
    if (carouselItems.length < 10) {
      // add new initial item to carousel items array
      // CLICK_THROUGH_LINK, CALL_TO_ACTION from previous item must be added to new item
      const newCarouselItem = {
        ...initialCarouselItem,
        [CLICK_THROUGH_LINK]: getIn(values, `${itemPath}.${CLICK_THROUGH_LINK}`),
        [CALL_TO_ACTION]: getIn(values, `${itemPath}.${CALL_TO_ACTION}`)
      }
      const updatedValues = {
        [CAROUSEL_ITEMS]: [...carouselItems, newCarouselItem],
        [CAROUSEL_ITEMS_LENGTH]: carouselItemsLength + 1
      }

      dispatch(setAdCarouselSelectedIndex(selectedItemIndexKey, carouselItems.length))
      setValues({ ...values, ...updatedValues })
    }

    // set a showErrors value to false to ensure that the next card
    // does not display validation errors before clicking on continue button
    setShowErrors(false)
  }, [carouselItems, values, itemPath, carouselItemsLength, dispatch, selectedItemIndexKey, setValues])

  // Set selected index to the clicked item index, and focus the item
  const handleItemClick = useCallback(
    ({ index, isFormOpened }) => {
      dispatch(setAdCarouselSelectedIndex(selectedItemIndexKey, index))
      setFieldValue(IS_ITEM_FORM_OPENED, isFormOpened)
    },
    [dispatch, selectedItemIndexKey, setFieldValue]
  )

  const handleDeleteItem = useCallback(
    index => {
      // delete item from carousel items array
      const newCarouselItems = [...carouselItems]
      newCarouselItems.splice(index, 1)

      const updatedValues = {
        [CAROUSEL_ITEMS]: newCarouselItems,
        [CAROUSEL_ITEMS_LENGTH]: carouselItemsLength - 1
      }

      // select previous item if deleted item wasn't first
      if (index !== 0) {
        dispatch(setAdCarouselSelectedIndex(selectedItemIndexKey, selectedItemIndex - 1))
      }
      setValues({ ...values, ...updatedValues })
      onCarouselItemDelete && onCarouselItemDelete(newCarouselItems)
    },
    [
      dispatch,
      carouselItems,
      setValues,
      values,
      selectedItemIndexKey,
      selectedItemIndex,
      carouselItemsLength,
      onCarouselItemDelete
    ]
  )

  return (
    <>
      {carouselItems.map((item, index) => (
        <CarouselItemForm
          key={index}
          formik={formik}
          item={item}
          index={index}
          deleteItem={() => handleDeleteItem(index)}
          onItemClick={handleItemClick}
          showErrors={showErrors}
          isStepFieldsValid={isFieldsValid}
          campaignObjective={campaignObjective}
        />
      ))}
      {carouselItems.length < 10 && (
        <Button type="button" className="btnBlock" onClick={handleAddAnotherItem}>
          {t('Add Another Image or Video')}
        </Button>
      )}
      {showErrors && carouselItemsLengthErrors && <ErrorMessage error={carouselItemsLengthErrors} />}
    </>
  )
}

CarouselMediaFields.propTypes = {
  formik: PropTypes.object,
  isFieldsValid: PropTypes.func,
  onCarouselItemDelete: PropTypes.func
}

export default CarouselMediaFields
